// ── Friet Fighter — per-fighter move-tabellen (M3: De Vechtstijlen) ──
// De gedeelde normalen (MOVES in data.ts) blijven voor iedereen gelijk; de
// SIGNATURE-special differentieert de 9 vechters. Frame-data is sim-domein →
// dit leeft in sim/, niet in config/ (de sim blijft self-contained).
// v1: precies één special per vechter. Vol kit (3 specials + eigen super +
// Vetslag) is bewust uitgesteld — zie ROADMAP.md M3.

import type { Move } from './types';
import type { Motion } from './motion';
import { MOVES, MAX_METER } from './data';

export type SpecialType = 'projectile' | 'dp' | 'command-grab' | 'dash' | 'normal';

export interface ProjSpawn {
  speed: number; // units/frame (facing wordt ingebakken bij spawn)
  lifetime: number; // frames
  hw: number; // halve breedte hitbox
  hh: number; // halve hoogte hitbox
  spawnDx: number; // spawn-offset vóór de vechter
  spawnY: number; // spawn-hoogte
}

export interface SpecialMove extends Move {
  motion: Motion; // 236/214/623
  special: SpecialType;
  meterCost: number; // meter-kosten bij activatie
  meterGainHit: number; // extra meter-winst bij raak (bovenop de basis)
  proj?: ProjSpawn; // alleen voor special === 'projectile'
  lunge?: number; // voorwaartse units/frame tijdens active (special === 'dash')
  unblockable?: boolean; // negeert block (special === 'command-grab')
}

export function isSpecial(m: Move | SpecialMove): m is SpecialMove {
  return (m as SpecialMove).motion !== undefined;
}

// ── Frikandel — Frikandel Speciaal (236, projectiel) — het bewijs-stuk ──
const frikandelSpeciaal: SpecialMove = {
  id: 'frikandelSpeciaal',
  name: 'Frikandel Speciaal',
  startup: 12,
  active: 6,
  recovery: 18,
  damage: 60,
  chip: 10,
  hitstun: 18,
  blockstun: 12,
  pushHit: 0.12,
  pushBlock: 0.08,
  // Geen eigen fighter-hitbox: het projectiel draagt de hit (activeHitboxOf → null).
  hbXMin: 0,
  hbXMax: 0,
  hbYMin: 0,
  hbYMax: 0,
  motion: 'qcf',
  special: 'projectile',
  meterCost: 25,
  meterGainHit: 8,
  proj: { speed: 0.17, lifetime: 110, hw: 0.34, hh: 0.34, spawnDx: 0.75, spawnY: 1.1 },
};

// ── Kroket — Ragout-golf (236, projectiel: tragere, hogere golf) ──
const ragoutGolf: SpecialMove = {
  id: 'ragoutGolf', name: 'Ragout-golf',
  startup: 14, active: 6, recovery: 20, damage: 55, chip: 9, hitstun: 18, blockstun: 12, pushHit: 0.12, pushBlock: 0.08,
  hbXMin: 0, hbXMax: 0, hbYMin: 0, hbYMax: 0,
  motion: 'qcf', special: 'projectile', meterCost: 25, meterGainHit: 8,
  proj: { speed: 0.14, lifetime: 100, hw: 0.4, hh: 0.5, spawnDx: 0.7, spawnY: 1.0 },
};

// ── Kaassoufflé — Kaas-flik (236, projectiel: snel & laag, zoner) ──
const kaasFlik: SpecialMove = {
  id: 'kaasFlik', name: 'Kaas-flik',
  startup: 10, active: 5, recovery: 22, damage: 45, chip: 7, hitstun: 16, blockstun: 11, pushHit: 0.1, pushBlock: 0.07,
  hbXMin: 0, hbXMax: 0, hbYMin: 0, hbYMax: 0,
  motion: 'qcf', special: 'projectile', meterCost: 25, meterGainHit: 8,
  proj: { speed: 0.21, lifetime: 120, hw: 0.3, hh: 0.3, spawnDx: 0.7, spawnY: 0.9 },
};

// ── Berenhap — Verre Port (236, dash: lange-reik lunge met de prikker) ──
const verrePort: SpecialMove = {
  id: 'verrePort', name: 'Verre Port',
  startup: 11, active: 5, recovery: 22, damage: 80, chip: 12, hitstun: 20, blockstun: 14, pushHit: 0.16, pushBlock: 0.1,
  hbXMin: 0.5, hbXMax: 2.7, hbYMin: 0.5, hbYMax: 1.35,
  motion: 'qcf', special: 'dash', meterCost: 25, meterGainHit: 8, lunge: 0.16,
};

// ── Kipcorn — Zoef (236, dash: razendsnelle korte lunge) ──
const zoef: SpecialMove = {
  id: 'zoef', name: 'Zoef',
  startup: 6, active: 4, recovery: 16, damage: 55, chip: 8, hitstun: 17, blockstun: 12, pushHit: 0.14, pushBlock: 0.09,
  hbXMin: 0.4, hbXMax: 1.7, hbYMin: 0.5, hbYMax: 1.4,
  motion: 'qcf', special: 'dash', meterCost: 25, meterGainHit: 8, lunge: 0.3,
};

// ── Bitterbal — Bowling-rol (214, command-grab: kort, onblokbaar, hoge schade) ──
const bowlingRol: SpecialMove = {
  id: 'bowlingRol', name: 'Bowling-rol',
  startup: 9, active: 4, recovery: 26, damage: 110, chip: 0, hitstun: 28, blockstun: 0, pushHit: 0.2, pushBlock: 0,
  hbXMin: 0.3, hbXMax: 1.05, hbYMin: 0, hbYMax: 1.4,
  motion: 'qcb', special: 'command-grab', meterCost: 35, meterGainHit: 6, unblockable: true,
};

// ── Bamiblok — Mie-schild (214, normal-utility: trage zware tegenstoot) ──
const mieSchild: SpecialMove = {
  id: 'mieSchild', name: 'Mie-schild',
  startup: 12, active: 6, recovery: 20, damage: 70, chip: 11, hitstun: 20, blockstun: 14, pushHit: 0.18, pushBlock: 0.12,
  hbXMin: 0.4, hbXMax: 1.5, hbYMin: 0.4, hbYMax: 1.6,
  motion: 'qcb', special: 'normal', meterCost: 25, meterGainHit: 8,
};

// ── Eierbal — Drijf (214, normal-utility: zwevende veeg) ──
const drijf: SpecialMove = {
  id: 'drijf', name: 'Drijf',
  startup: 8, active: 6, recovery: 18, damage: 60, chip: 9, hitstun: 18, blockstun: 12, pushHit: 0.15, pushBlock: 0.1,
  hbXMin: 0.4, hbXMax: 1.5, hbYMin: 0.6, hbYMax: 1.9,
  motion: 'qcb', special: 'normal', meterCost: 25, meterGainHit: 8,
};

// ── Loempia — Schaduw-slag (214, normal-utility: snelle counter-poke) ──
const schaduwSlag: SpecialMove = {
  id: 'schaduwSlag', name: 'Schaduw-slag',
  startup: 6, active: 4, recovery: 20, damage: 65, chip: 9, hitstun: 18, blockstun: 12, pushHit: 0.14, pushBlock: 0.09,
  hbXMin: 0.4, hbXMax: 1.5, hbYMin: 0.4, hbYMax: 1.5,
  motion: 'qcb', special: 'normal', meterCost: 25, meterGainHit: 8,
};

// ── Boss: Patatje Oorlog ──
// Koude Golf (236, projectiel: trage, brede koude-saus-golf, hoge schade) +
// een armored Stollende Klap (de boss eet één treffer tijdens active → de speler
// kan hem niet simpel weg-zonen). Zie ROADMAP.md M4; volle multi-fase + Koude Golf-
// super uitgesteld.
const koudeGolf: SpecialMove = {
  id: 'koudeGolf', name: 'Koude Golf',
  startup: 16, active: 6, recovery: 24, damage: 85, chip: 15, hitstun: 20, blockstun: 14, pushHit: 0.16, pushBlock: 0.1,
  hbXMin: 0, hbXMax: 0, hbYMin: 0, hbYMax: 0,
  motion: 'qcf', special: 'projectile', meterCost: 25, meterGainHit: 8,
  proj: { speed: 0.15, lifetime: 130, hw: 0.46, hh: 0.55, spawnDx: 0.85, spawnY: 1.1 },
};

const stollendeKlap: Move = {
  ...MOVES.standHeavy,
  id: 'standHeavy', name: 'Stollende Klap',
  startup: 12, recovery: 20, damage: 120, hbXMax: 1.9,
  armorHits: 1, // hyper-armor tijdens active
};

// ── 2e specials ──────────────────────────────────────────────────────────────
// Elke vechter krijgt een tweede special met een motion die NIET botst met zijn
// signature (signatures gebruiken qcf/qcb → dp/623 is vrij). Archetype-passend:
// zoners krijgen een anti-air (dp), de grappler een gap-closer (dash), etc.
// dp/normal hebben geen aparte engine-logica → gewone melee op het 623/214/236-input.
const A = (id: string, name: string, motion: Motion, special: SpecialType, fd: Partial<SpecialMove>): SpecialMove => ({
  id, name, startup: 8, active: 5, recovery: 18, damage: 60, chip: 9, hitstun: 18, blockstun: 12,
  pushHit: 0.13, pushBlock: 0.08, hbXMin: 0.3, hbXMax: 1.3, hbYMin: 0.4, hbYMax: 1.5,
  motion, special, meterCost: 25, meterGainHit: 8, ...fd,
});

// Anti-airs (dp/623): snelle rijzende uithaal, hoge hitbox.
const frituurStoot = A('frituurStoot', 'Frituur-stoot', 'dp', 'dp', { startup: 5, active: 6, recovery: 24, damage: 70, hbYMin: 0.8, hbYMax: 2.3, hbXMax: 1.2 });
const korstStoot = A('korstStoot', 'Korst-stoot', 'dp', 'dp', { startup: 6, active: 6, recovery: 24, damage: 72, hbYMin: 0.8, hbYMax: 2.3, hbXMax: 1.2 });
const kipWip = A('kipWip', 'Kip-wip', 'dp', 'dp', { startup: 4, active: 5, recovery: 22, damage: 60, hbYMin: 0.8, hbYMax: 2.2, hbXMax: 1.1 });
const loempiaWervel = A('loempiaWervel', 'Loempia-wervel', 'dp', 'dp', { startup: 5, active: 6, recovery: 22, damage: 66, hbYMin: 0.7, hbYMax: 2.2, hbXMax: 1.3 });
// Kaassoufflé: snelle evasieve poke (qcb).
const souffleZweef = A('souffleZweef', 'Soufflé-zweef', 'qcb', 'normal', { startup: 7, active: 4, recovery: 16, damage: 52, hbXMax: 1.6 });
// Berenhap: trage zware stamp (qcb).
const berenStamp = A('berenStamp', 'Beren-stamp', 'qcb', 'normal', { startup: 14, active: 5, recovery: 24, damage: 88, hbXMax: 1.7, hbYMax: 1.7, pushHit: 0.2, armorHits: 1 });
// Bitterbal: dash-gap-closer (qcf) — zet de command-grab op.
const bitterStorm = A('bitterStorm', 'Bitterbal-stormloop', 'qcf', 'dash', { startup: 8, active: 5, recovery: 20, damage: 58, lunge: 0.26, hbXMin: 0.4, hbXMax: 1.7 });
// Bamiblok + Eierbal: een tweede projectiel (qcf).
const bamiBal = A('bamiBal', 'Bami-bal', 'qcf', 'projectile', { startup: 13, active: 6, recovery: 20, damage: 50, hbXMin: 0, hbXMax: 0, hbYMin: 0, hbYMax: 0, proj: { speed: 0.15, lifetime: 105, hw: 0.36, hh: 0.4, spawnDx: 0.7, spawnY: 1.0 } });
const eiWorp = A('eiWorp', 'Ei-worp', 'qcf', 'projectile', { startup: 11, active: 5, recovery: 20, damage: 48, hbXMin: 0, hbXMax: 0, hbYMin: 0, hbYMax: 0, proj: { speed: 0.18, lifetime: 115, hw: 0.32, hh: 0.32, spawnDx: 0.7, spawnY: 1.2 } });

/** Per-fighter move-tabel = gedeelde normalen + de eigen special(s). */
export const MOVE_TABLES: Record<string, Record<string, Move>> = {
  frikandel: { ...MOVES, frikandelSpeciaal, frituurStoot },
  kroket: { ...MOVES, ragoutGolf, korstStoot },
  kaassouffle: { ...MOVES, kaasFlik, souffleZweef },
  berenhap: { ...MOVES, verrePort, berenStamp },
  kipcorn: { ...MOVES, zoef, kipWip },
  bitterbal: { ...MOVES, bowlingRol, bitterStorm },
  bamiblok: { ...MOVES, mieSchild, bamiBal },
  eierbal: { ...MOVES, drijf, eiWorp },
  loempia: { ...MOVES, schaduwSlag, loempiaWervel },
  patatje_oorlog: { ...MOVES, standHeavy: stollendeKlap, koudeGolf },
};

// Bindbare specials per vechter (signature + 2e; super wordt apart getriggerd).
const SECOND: Record<string, string> = {
  frikandel: 'frituurStoot', kroket: 'korstStoot', kaassouffle: 'souffleZweef',
  berenhap: 'berenStamp', kipcorn: 'kipWip', bitterbal: 'bitterStorm',
  bamiblok: 'bamiBal', eierbal: 'eiWorp', loempia: 'loempiaWervel',
};

/** Move-tabel voor een vechter; valt terug op de gedeelde normalen. */
export function movesFor(charId: string): Record<string, Move> {
  return MOVE_TABLES[charId] ?? MOVES;
}

// v1-signature per vechter (precies één special per archetype).
const SIGNATURE: Record<string, string> = {
  frikandel: 'frikandelSpeciaal', // 236 projectiel
  kroket: 'ragoutGolf', // 236 projectiel
  kaassouffle: 'kaasFlik', // 236 projectiel
  berenhap: 'verrePort', // 236 dash (lange reik)
  kipcorn: 'zoef', // 236 dash (snel)
  bitterbal: 'bowlingRol', // 214 command-grab
  bamiblok: 'mieSchild', // 214 normal-utility
  eierbal: 'drijf', // 214 normal-utility
  loempia: 'schaduwSlag', // 214 normal-utility
  patatje_oorlog: 'koudeGolf', // 236 projectiel (boss)
};

/** De signature-special van een vechter (of null als hij er nog geen heeft). */
export function signatureFor(charId: string): SpecialMove | null {
  const id = SIGNATURE[charId];
  const m = id ? MOVE_TABLES[charId]?.[id] : undefined;
  return m && isSpecial(m) ? m : null;
}

/** Alle motion-bindbare specials van een vechter (signature + 2e). Super = apart. */
export function specialsFor(charId: string): SpecialMove[] {
  const table = MOVE_TABLES[charId];
  if (!table) return [];
  return [SIGNATURE[charId], SECOND[charId]]
    .map((id) => (id ? table[id] : undefined))
    .filter((m): m is SpecialMove => !!m && isSpecial(m));
}

/** De special waarvan de motion nu matcht en die de speler kan betalen (of null). */
export function matchedSpecial(charId: string, motion: Motion | null, meter: number): SpecialMove | null {
  if (!motion) return null;
  return specialsFor(charId).find((s) => s.motion === motion && meter >= s.meterCost) ?? null;
}

// ── Level-3 supers (Vetslag) ─────────────────────────────────────────────────
// Eén super per vechter: een opgevoerde versie van de signature die de hele meter
// kost (MAX_METER), super-armor heeft en ~2× schade doet. Hergebruikt de animatie
// + FX van de signature (powered-up gevoel) → SUPER_ANIM mapt de super-id terug.
const SUPER_NAMES: Record<string, string> = {
  frikandel: 'Frikandel Razernij',
  kroket: 'Ragout-storm',
  kaassouffle: 'Kaas-lawine',
  berenhap: 'Berenklauw-storm',
  kipcorn: 'Kip-stormloop',
  bitterbal: 'Volle Worp',
  bamiblok: 'Bami-banzai',
  eierbal: 'Eierbal-explosie',
  loempia: 'Schaduw-storm',
  patatje_oorlog: 'Oerknal',
};

function makeSuper(charId: string, sig: SpecialMove): SpecialMove {
  return {
    ...sig,
    id: `${sig.id}_super`,
    name: SUPER_NAMES[charId] ?? `${sig.name} (super)`,
    damage: Math.min(200, Math.round(sig.damage * 2.2)), // cap → geen super die ~kwart leven oneshot (bv. command-grab)
    chip: Math.round(sig.chip * 1.6),
    hitstun: sig.hitstun + 6,
    blockstun: sig.blockstun + 4,
    pushHit: sig.pushHit + 0.06,
    meterCost: MAX_METER,
    meterGainHit: 0,
    armorHits: 1, // super-armor op de uithaal
    hbXMax: sig.hbXMax ? sig.hbXMax + 0.4 : sig.hbXMax,
    hbYMax: sig.hbYMax ? sig.hbYMax + 0.25 : sig.hbYMax,
    lunge: sig.lunge ? sig.lunge * 1.2 : sig.lunge,
    proj: sig.proj
      ? { ...sig.proj, hw: sig.proj.hw * 1.6, hh: sig.proj.hh * 1.6, speed: sig.proj.speed * 1.1, lifetime: sig.proj.lifetime + 20 }
      : undefined,
  };
}

/** super-id → animatie-id (= de signature, voor render-hergebruik). */
export const SUPER_ANIM: Record<string, string> = {};
const SUPERS: Record<string, SpecialMove> = {};
for (const [cid, sigId] of Object.entries(SIGNATURE)) {
  const sig = MOVE_TABLES[cid]?.[sigId];
  if (!sig || !isSpecial(sig)) continue;
  const sup = makeSuper(cid, sig);
  SUPERS[cid] = sup;
  SUPER_ANIM[sup.id] = sigId; // super hergebruikt de signature-animatie
  MOVE_TABLES[cid][sup.id] = sup; // zodat movesFor() de super-hitbox/frame-data vindt
}

/** De super (level-3) van een vechter (of null). Kost de volle meter. */
export function superFor(charId: string): SpecialMove | null {
  return SUPERS[charId] ?? null;
}

// ── Boss fase-2 (enrage) ───────────────────────────────────────────────────────
// Patatje Oorlog slaat onder de helft HP om naar fase 2: de AI wordt agressiever
// (zie ai/controller.ts) én hij krijgt hyper-armor op zijn active frames zodat hij
// door pokes heen trade't (zie engine.applyHit). Puur afgeleid van health → geen
// extra state, blijft deterministisch en replay-safe.
export const BOSS_CHAR_ID = 'patatje_oorlog';
export const BOSS_ENRAGE_FRAC = 0.5;

/** Is dit de boss in fase 2 (op/onder de enrage-drempel)? Pure functie van health. */
export function isEnraged(f: { charId: string; health: number; maxHealth: number }): boolean {
  return f.charId === BOSS_CHAR_ID && f.health <= f.maxHealth * BOSS_ENRAGE_FRAC;
}
